import { useEffect, useState, useContext } from "react"

//PrimeReact
import { Accordion, AccordionTab } from "primereact/accordion"
import { ProgressSpinner } from "primereact/progressspinner"
import { Paginator } from "primereact/paginator"

// Context
import { UserContext } from "../../../context/userContext"

//Helpers
import { LogDebug } from "../../../helpers/logger"

//Tasks modules
import {
  taskApiGetAssignedTasksPOST,
  TaskApiMarkCompletePUT,
} from "../TasksApi"
import TaskDisplayPanelAssigned from "./TaskDisplayPanelAssigned"

let lastRefresh = {
  pending: new Date().getTime(),
  completed: new Date().getTime(),
}

export default function TaskAssigned(props) {
  const userCtx = useContext(UserContext)

  const [loading, setLoading] = useState(false)
  const [pendingPageSize, setPendingPageSize] = useState(10)
  const [pendingTasks, setPendingTasks] = useState(null)
  const [completedTasks, setCompletedTasks] = useState(null)
  const [completedPageSize, setCompletedPageSize] = useState(10)
  const [pendingCurrentPage, setPendingCurrentPage] = useState(1)
  const [completedCurrentPage, setCompletedCurrentPage] = useState(1)
  const [activePanel, setActivePanel] = useState(0)

  useEffect(() => {
    if (!pendingTasks) {
      getAssignedTasks(1, pendingPageSize, "pending")
    } else if (!completedTasks) {
      getAssignedTasks(1, completedPageSize, "completed")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingTasks, completedTasks])

  useEffect(() => {
    setActivePanel(props.activeOptionPanel || 0)
  }, [props.activeOptionPanel])

  useEffect(() => {
    if (props.refreshTasks > lastRefresh["pending"]) {
      getAssignedTasks(1, pendingPageSize, "pending")
    }
    if (props.refreshTasks > lastRefresh["completed"]) {
      getAssignedTasks(1, completedPageSize, "complete")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.refreshTasks])

  async function getAssignedTasks(page, pageSize, ...types) {
    setLoading(true)

    for (const type of types) {
      try {
        // Limit refreshes
        if (new Date().getTime() - lastRefresh[type] < 1000) continue
        lastRefresh[type] = new Date().getTime()

        const res = await taskApiGetAssignedTasksPOST(userCtx.tokenTask, {
          groupId: userCtx.session.user.organization_id,
          userId: userCtx.session.user.staff_id,
          pageNum:
            page ||
            (props.accordionKey === "0"
              ? pendingCurrentPage
              : completedCurrentPage),
          pageSize: pageSize
            ? pageSize
            : type === "pending"
            ? pendingPageSize
            : completedPageSize,
          status: type,
        })

        if (res) {
          if (type === "pending") setPendingTasks(res)
          else setCompletedTasks(res)
        }

        if (res.numTotal > 0) {
          props.checkUserCache(res.items.map((x) => x.createdByUserId))
        }

        LogDebug(`Created Tasks [${type}]`, res)
        setLoading(false)
      } catch (error) {
        console.error(`Unable to list ${type} created tasks`, error)
        props.onShowError("Unable to list tasks. See console for details.")
        setLoading(false)
      }
    }
  }

  const onPendingPageChange = (event) => {
    if (pendingPageSize !== event.rows) {
      setPendingPageSize(event.rows)
    }
    setPendingCurrentPage(event.page + 1)

    getAssignedTasks(event.page + 1, event.rows, "pending")
  }

  const onCompletedPageChange = (event) => {
    if (completedPageSize !== event.rows) {
      setCompletedPageSize(event.rows)
    }
    setCompletedCurrentPage(event.page + 1)

    getAssignedTasks(event.page + 1, event.rows, "complete")
  }

  async function markTaskAsDone(taskId) {
    setLoading(true)
    try {
      await TaskApiMarkCompletePUT(userCtx.tokenTask, taskId)
      props.onRefreshTasks(new Date().getTime())
      LogDebug(`Task marked as complete (ID: ${taskId})`)
      setLoading(false)
    } catch (error) {
      console.error(`Unable to mark task as complete (id: ${taskId})`, error)
      props.onShowError(
        `Unable to mark task as complete (id: ${taskId}). See console for details.`
      )
      setLoading(false)
    }
  }

  return (
    <div>
      {loading || props.parentLoading ? (
        <div style={{ textAlign: "center" }}>
          <ProgressSpinner />
        </div>
      ) : (
        <div style={{ width: "100%" }}>
          <Accordion
            activeIndex={activePanel}
            onTabChange={(e) => {
              setActivePanel(e.index)
            }}
            style={{ width: "100%" }}
          >
            <AccordionTab
              header={`Pending tasks [${
                pendingTasks ? pendingTasks.items.length : 0
              }]`}
            >
              <div style={{ display: "flex", flexDirection: "row" }}>
                <div style={{ flexDirection: "column", width: "100%" }}>
                  {pendingTasks
                    ? pendingTasks.items.map((x) => {
                        return <TaskDisplayPanelAssigned task={x} key={x.id} getActionButton={props.getActionButton} getUser={props.getUser} markTaskAsDone={markTaskAsDone} />
                      })
                    : null}
                </div>
              </div>
              {pendingTasks ? (
                <Paginator
                  first={pendingTasks.pageSize * (pendingTasks.pageNum - 1)}
                  rows={pendingTasks.pageSize}
                  totalRecords={pendingTasks.numTotal}
                  onPageChange={onPendingPageChange}
                  rowsPerPageOptions={[5, 10, 20, 30]}
                />
              ) : null}
            </AccordionTab>
            <AccordionTab header="Completed tasks">
              <div style={{ flexDirection: "column", width: "100%" }}>
                {completedTasks
                  ? completedTasks.items.map((x) => {
                      return <TaskDisplayPanelAssigned task={x} key={x.id} getActionButton={props.getActionButton} getUser={props.getUser} markTaskAsDone={markTaskAsDone} />
                    })
                  : null}
              </div>
              {completedTasks ? (
                <Paginator
                  first={completedTasks.pageSize * (completedTasks.pageNum - 1)}
                  rows={completedTasks.pageSize}
                  totalRecords={completedTasks.numTotal}
                  onPageChange={onCompletedPageChange}
                  rowsPerPageOptions={[5, 10, 20, 30]}
                />
              ) : null}
            </AccordionTab>
          </Accordion>
        </div>
      )}
    </div>
  )
}
